import React, { useState, useEffect, useRef, useMemo } from "react";
import { PowerBIEmbed } from "powerbi-client-react";
import { models } from "powerbi-client";
import "./powerBi.css";
import {
  getPbiAccessToken,
  createUsageLog,
} from "../Redux/actions/utility-actions";
import { connect, useSelector, useDispatch } from "react-redux";
import { initializeEventListeners } from "../Components/Common/Function";
import _ from "lodash";

const PowerBi = ({
  getPbiAccessToken,
  createUsageLog,
  pbiAccessToken,
  pageName,
  id,
  dealerCode,
  wssCode,
  filterName,
  tableName = "Hierarchy",
  /** Tailwind height class or any CSS height string.
   *  Examples:
   *    height="h-[75vh]"          → Tailwind arbitrary value
   *    height="h-screen"          → full viewport
   *    height="calc(100vh - 56px)"→ raw CSS (applied via style prop)
   *  Defaults to calc(100vh - 240px) matching the original CSS.
   */
  height,
}) => {
  const dispatch = useDispatch();
  const eventCountRef = useRef(0);
  const hasEventListeners = useRef(false);

  const userDetails = useSelector((state) => state?.userDetails?.details);
  const userEmail = userDetails?.Email;
  const title = userDetails?.Title;
  const nsmEmail = useSelector((state) => state?.HoUserSh7Code?.nsmEmail);
  const sh7salesGroupCode = useSelector(
    (state) => state.SelectedSalesGroupCode?.SalesGroupCode
  );

  const salesGroupCode = useSelector(
    (state) => state?.userDetails?.details?.Sales_Group_Code__c
  );

  const UserType = useSelector((state) =>
    state?.userType?.type !== "Other"
      ? state?.userType?.type
      : userDetails?.Title
  );
  const expiryTime = useSelector((state) => state?.tokens?.creationDate);

  const [view, setView] = useState();
  const [columnName, setColumnName] = useState("");
  const [columnNamePageID, setColumnNamePageID] = useState("");
  const [pbiFilter, setPbiFilter] = useState([]);
  const [emailTableName, setEmailTableName] = useState(tableName);
  const [reload, setReload] = useState(false);

  const pageId = pageName?.id;

  // Memoized PBI Email based on user type
  const pbiEmail = useMemo(() => {
    if (
      (UserType === "HO Collaborator" || UserType=== "TMM" || UserType === "HO Auditor") &&
      nsmEmail
    ) {
      return [nsmEmail];
    }
    return userEmail ? [userEmail] : [];
  }, [UserType, nsmEmail, userEmail]);

  let pbiSalesgroupCode = useMemo(() => {
    if (
      (UserType === "HO Collaborator" || UserType === "TMM" || UserType === "HO Auditor") &&
      sh7salesGroupCode
    ) {
      return [sh7salesGroupCode];
    }
    return salesGroupCode ? [salesGroupCode] : [];
  }, [UserType, salesGroupCode, sh7salesGroupCode]);

  const isReadyToRender = useMemo(() => {
    return pbiAccessToken && view && pbiFilter.length > 0 && columnName;
  }, [pbiAccessToken, view, pbiFilter, columnName]);

  // Update column name based on UserType
  useEffect(() => {
    if (UserType) {
      const columnMap = {
        SH7: "SH7Email",
        SH6: "SH5Email",
        SH5: "SH5Email",
        SH4: "SH4Email",
        "HO Collaborator": "SH7Email",
        "TMM":"SH7Email",
        "HO Auditor": "SH7Email",
        "RBDM Collaborator": "SH3Email",
        MH3: "SH3Email",
      };
      const columnMapPageId = {
        SH7: "Sh7Email",
        SH6: "Sh5Email",
        SH5: "Sh5Email",
        SH4: "Sh4Email",
        "HO Collaborator": "Sh7Email",
        "TMM": "Sh7Email",
        "HO Auditor": "Sh7Email",
        "RBDM Collaborator": "Sh3Email",
        MH3: "Sh3Email",
      };

      const newColumnName = columnMap[UserType] || "SH3Email";
      const newcolumnMapPageId = columnMapPageId[UserType] || "Sh3Email";
      setColumnName(newColumnName);
      setColumnNamePageID(newcolumnMapPageId);
    }
  }, [UserType]);

  useEffect(() => {
    // Generate filters whenever columnName or emailTableName changes
    if (columnName && emailTableName) {
      generateFilters(columnName, emailTableName, pageId, UserType, id);
    }
  }, [columnName, emailTableName, pageId, UserType, id]);

  useEffect(() => {
    if (filterName) {
      setEmailTableName(filterName);
    }
  }, [filterName]);

  useEffect(() => {
    // Initialize event listeners for token expiration
    const cleanup = initializeEventListeners(
      expiryTime,
      dispatch,
      eventCountRef,
      hasEventListeners
    );
    return cleanup;
  }, [expiryTime, dispatch]);

  useEffect(() => {
    // Generate filters whenever wssCode or dealerCode changes
    if (columnName && emailTableName) {
      generateFilters(columnName, emailTableName, pageId, UserType, id);
    }
  }, [wssCode, dealerCode, columnName, emailTableName, pageId, UserType, id]);

  useEffect(() => {
    // Handle view layout and token retrieval
    const updateView = () => {
      if (window.matchMedia("(max-width: 600px)").matches) {
        setView(models.LayoutType.MobilePortrait);
      } else {
        setView(models.LayoutType.MobileLandscape);
      }
    };

    updateView();
    if (userEmail) {
      getPbiAccessToken(userEmail, id);
      createUsageLog(userEmail, id, pageName?.id, pageName?.name);
    }
    window.addEventListener("resize", updateView);
    return () => window.removeEventListener("resize", updateView);
  }, [getPbiAccessToken, userEmail]);

  const generateFilters = (columnName, emailTableName, pageId, UserType, reportId) => {
    let commonFilter;

    // 🔹 Define defaults
    let targetTable = emailTableName;
    let targetColumn = columnName;

    // ✅ 2️⃣ Then check for HO Collaborator / Auditor for other reports
    if (UserType === "HO Collaborator" || UserType === "TMM" || UserType === "HO Auditor") {
      switch (pageId) {
        case "f5339b70d5400b4d66d9": // PCC Overview Dashboard
          targetTable = "Loyalty Tier Ref";
          targetColumn = "SG";
          break;

        case "4d8aeed31c46261754ea": // WSS Speedometer - MNT & ASF
        case "97cae0c421b56c060431": // WSS Speedometer
          targetTable = "DimCustomer";
          targetColumn = "SaleGroupCode";
          break;

        case "db748da0b2074365a019": // My Dealer
        case "13d818c72971a4547786": // My WSS
        case "249647d9a88019e28db5": // My Team
          targetTable = "SalesGroup";
          targetColumn = "SalesGroupCode";
          break;

        case "223cd674c97ddc2da176": // Branch Performance Index - FV
        case "44fc726661060e5f4f3d": // Branch Performance Index - Araldite
        case "7de9d97612731f282565":
        case "89b5290e50834ee74325":
        case "d5a214c4e18e6567daad":
        case "e540c588abdd1363d6e1":
        case "dde204f9b616fc883970":
        case "25ed67a88267d3dcf2a2":
          targetTable = "SH2Master";
          targetColumn = "SalesGroup";
          break;

        case "b8ed9801a66e9699a458": // PIL Similar Survey & KD/LCD Performance
          // Check by Report ID
          if (reportId === "1e8069b9-85d5-416c-ab1c-d7583a3a7e48") {
            // PIL Similar Survey Report
            targetTable = "Dimsalesgroup";
            targetColumn = "salegroupcode";
          } else if (reportId === "e0eb2599-b39d-4b21-919e-4c5a1bd8ed69") {
            // KD/LCD Performance
            targetTable = "SH2Master";
            targetColumn = "SalesGroup";
          }
          break;

        case "fea60e84-eaf3-48a4-90ad-795360fb0878":
          if(pageName.name == "TSI Market Working - FTD" ){
            targetTable = "Dimsalesgroup";
            targetColumn = "Salegroupcode";
          }else if(pageName.name == "Distributor Invoicing - FTD"){
            targetTable = "DimSalesGroup";
            targetColumn = "SaleGroupCode";
          }
          else{
            targetTable = "Pidilite daily sales  secondary";
            targetColumn = "salesgroupcode";
          }
          
          break;

          case "29546b7646e42d34609d":
            targetTable = "VW_SH2Master";
            targetColumn = "SalesGroup";

        default:
          console.log("⚠️ Unknown Page ID for HO user:", pageId);
      }
    }

    // ✅ 3️⃣ Fallback for everyone else
    else {
      if (pageId === "fea60e84-eaf3-48a4-90ad-795360fb0878" && pageName.name == "Daily Sales Report"){
        targetColumn = columnNamePageID;
      }
      
      // Handle PIL Similar Survey for non-HO users
      if (pageId === "b8ed9801a66e9699a458" && reportId === "1e8069b9-85d5-416c-ab1c-d7583a3a7e48") {
        // PIL Similar Survey: Use DimHierarchy table for filtering
        targetTable = "DimHierarchy";
        targetColumn = columnName.replace("Email", "email"); // SH3email, SH4email, etc.
      }

      pbiSalesgroupCode = pbiEmail;
    }

    // ✅ Build the filter
    commonFilter = {
      $schema: "http://powerbi.com/product/schema#basic",
      target: { table: targetTable, column: targetColumn },
      operator: "In",
      values:
        pbiSalesgroupCode && pbiSalesgroupCode.length > 0
          ? pbiSalesgroupCode
          : pbiEmail,
    };
    
    // --- Apply filters based on codes ---
    if (!_.isEmpty(wssCode)) {
      setPbiFilter([
        {
          $schema: "http://powerbi.com/product/schema#basic",
          target: { table: "Customer", column: "CustomerCode" },
          operator: "EqualTo",
          values: [wssCode],
        },
        {
          $schema: "http://powerbi.com/product/schema#basic",
          target: { table: "DimCustomer", column: "customercode" },
          operator: "EqualTo",
          values: [wssCode],
        },
        commonFilter,
      ]);
    } else if (!_.isEmpty(dealerCode)) {
      // Build dealer filters based on page
      const dealerFilters = [
        {
          $schema: "http://powerbi.com/product/schema#basic",
          target: { table: "Dealer", column: "DealerCode" },
          operator: "EqualTo",
          values: [dealerCode],
        },
        {
          $schema: "http://powerbi.com/product/schema#basic",
          target: { table: "Loyalty Tier Ref", column: "DealerCode" },
          operator: "EqualTo",
          values: [dealerCode],
        },
      ];

      // Add DimHierarchy filter for PIL Similar Survey Report (check by Report ID)
      if (id === "1e8069b9-85d5-416c-ab1c-d7583a3a7e48") {
        dealerFilters.push({
          $schema: "http://powerbi.com/product/schema#basic",
          target: { table: "Dimdealer_ms", column: "dealercode" },
          operator: "EqualTo",
          values: [dealerCode],
        });
      }

      dealerFilters.push(commonFilter);
      setPbiFilter(dealerFilters);
    } else if (pbiSalesgroupCode.length > 0) {
      setPbiFilter([commonFilter]);
    } else {
      setPbiFilter([]);
    }
  };

  // Resolve height: if it looks like a Tailwind class use className, else inline style
  const heightStyle = { height: height || 'calc(100vh - 140px)' };

  return (
    <div
      className="w-full mb-4 bg-white shadow-[0.081rem_0.081rem_0.348rem_rgba(20,20,20,0.07)] rounded-[0.525rem] !overflow-hidden"
      style={heightStyle}
    >
      {isReadyToRender && (
        <PowerBIEmbed
          embedConfig={{
            type: "report",
            id: id,
            embedUrl: "https://app.powerbi.com/reportEmbed/",
            pageName: pageName?.id,
            accessToken: pbiAccessToken,
            tokenType: models.TokenType.Aad,
            filters: pbiFilter,
            settings: {
              layoutType: view,
              panes: {
                filters: { expanded: false, visible: false },
              },
            },
          }}
          eventHandlers={
            new Map([
              ["loaded", () => {}],
              ["rendered", () => {}],
              ["error", (event) => console.error(event.detail)],
            ])
          }
          cssClassName={"report-style-class"}
          getEmbeddedComponent={(embeddedReport) => {
            window.report = embeddedReport;
          }}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  pbiAccessToken: state?.pbiToken?.pbiAccessToken,
});

export default connect(mapStateToProps, { getPbiAccessToken, createUsageLog })(
  PowerBi
);
